import { FC } from 'react';
import { useCmsMemberInfo } from '@doda/common/src/hooks/useCmsMemberInfo';
import { useAnalyticsSetup } from '@doda/common/src/hooks/useAnalytics';
import { Tag } from '@doda/common/src/components/Head/metaTag';
import { useMetaData } from '@doda/common/src/hooks/JobContent/JobTrend/useMetaData';
import {
  ANNOTATION_TEXT_1,
  ANNOTATION_TEXT_2,
  JOB_TREND,
  JOB_TREND_CARRY_ID,
  OTHER_JOB_TREND_TITLE,
} from '@doda/common/src/constants/seo/jobTrendConstants';
import {
  MEMBER_REGIST_LINK,
  LOGOUT_PARAM_PC,
  GUIDE_LOGIN_LINK,
  INTRO_LINK,
  REPLACE_ID,
  JOB_TREND_BREAD_CRUMB_TEXT,
} from '@doda/common/src/constants/seo/jobContentCommonConstants';
import { useSelector } from 'react-redux';
import { appQueryParamsSelector } from '@doda/common/src/redux/features/queryParams';
import { jobTrendRankingSelector } from '@doda/common/src/redux/features/JobContent/JobTrend/jobTrendRanking';
import { formatDate } from '@doda/common/src/logics/JobContent/createFormatDate';
import { BreadCrumb } from '@doda/common/src/interfaces/breadCrumb';
import styles from './index.module.scss';
import { AnchorLink } from '../../components/AnchorLink';
import { DiagnosisTools } from '../../../../../../../components/JobContent/DiagnosisTools';
import { HeaderTab } from '../../components/HeaderTab';
import { JobCountBargraphArea } from '../../components/JobCountBargraphArea';
import { MemberRegistForFooter } from '../../../../../../../components/JobContent/MemberRegistForFooter';
import { MemberRegistForRight } from '../../../../../../../components/JobContent/MemberRegistForRight';
import { OtherJobContentInfo } from '../../../../../../../components/JobContent/OtherJobContentInfo';
import { BreadCrumbList } from '../../../../../../../components/JobContent/BreadCrumbList';
import { FaqArea } from '../../components/FaqArea';
import { FaqLd } from '../../components/FaqLd';
import { OtherPageLink } from '../../components/OtherPageLink';

/**
 * JobTrendScreenのProps
 * @param {string} ページの一軸名
 * @param {boolean} ログイン状況
 * @param {BreadCrumb[]} パンくずリストデータ
 * @param {boolean} 求人ランキングのリンク表示可否
 *  */
export interface JobTrendScreenProps {
  targetName: string;
  isLogin: boolean;
  breadcrumbData: BreadCrumb[];
  isJobRankingLinkShow: boolean;
}
export const JobTrendScreen: FC<JobTrendScreenProps> = ({
  targetName,
  isLogin,
  breadcrumbData,
  isJobRankingLinkShow,
}) => {
  useCmsMemberInfo('pcHeader');
  useAnalyticsSetup();
  const { title, description, keywords, canonicalUrl } = useMetaData({
    targetName,
  });
  const publicationDate = formatDate(
    useSelector(jobTrendRankingSelector).publicationDate
  );
  const currentUrl = encodeURIComponent(canonicalUrl);

  // 会員登録ボタンエリアのリンク
  const carryId = JOB_TREND_CARRY_ID;
  const memberRegistLink = `${MEMBER_REGIST_LINK.replace(REPLACE_ID.CARRY_ID, carryId)}&${LOGOUT_PARAM_PC}${JOB_TREND}`;
  const guideLoginLink = `${GUIDE_LOGIN_LINK}?${LOGOUT_PARAM_PC}${JOB_TREND}&url=${currentUrl}`;
  const introLink = `${INTRO_LINK}?${LOGOUT_PARAM_PC}${JOB_TREND}`;

  const rankingList = useSelector(jobTrendRankingSelector);
  const queryParams = useSelector(appQueryParamsSelector);

  // フラグが年収（ha）で求人が1件以上あった場合のみ年収FAQ表示する
  const incomeData = rankingList.income[0].map(Number);
  const incomeLabel = rankingList.income[1].map(String);
  const incomeCombinedArray = incomeData.map((item, index) => [
    item,
    incomeLabel[index],
  ]);
  incomeCombinedArray.sort((a, b) => Number(b[0]) - Number(a[0]));
  const incomeShowFlg =
    queryParams.ha === undefined && incomeCombinedArray[0][0] > 0;

  // フラグが雇用形態（op=17,18,19）で求人が1件以上あった場合のみ雇用形態FAQ表示する
  const employmentStatusData = rankingList.employmentStatus[0].map(Number);
  const employmentStatusLabel = rankingList.employmentStatus[1].map(String);
  const employmentStatusCombinedArray = employmentStatusData.map(
    (item, index) => [item, employmentStatusLabel[index]]
  );
  employmentStatusCombinedArray.sort((a, b) => Number(b[0]) - Number(a[0]));
  let employmentStatusShowFlg = employmentStatusCombinedArray[0][0] > 0;
  if (employmentStatusShowFlg && queryParams.op) {
    employmentStatusShowFlg =
      queryParams.op[0] !== '17' &&
      queryParams.op[0] !== '18' &&
      queryParams.op[0] !== '19';
  }

  // 年収と雇用形態以外のFAQは求人が1件以上あった場合のみFAQ表示する
  const requirementsShowFlg = rankingList.requirements[0][0] > 0;
  const workplaceEnvironmentShowFlg =
    rankingList.workplaceEnvironment[0][0] > 0;
  const holidayAndWayOfWorkingShowFlg =
    rankingList.holidayAndWayOfWorking[0][0] > 0;

  // 全てのFAQ表示フラグがfalseだった場合、FAQエリア及びFAQスニペット自体画面出力しない
  const faqAreaShowFlg =
    requirementsShowFlg ||
    incomeShowFlg ||
    workplaceEnvironmentShowFlg ||
    holidayAndWayOfWorkingShowFlg ||
    employmentStatusShowFlg;
  return (
    <>
      <BreadCrumbList
        targetName={targetName}
        breadcrumbData={breadcrumbData}
        pageText={JOB_TREND_BREAD_CRUMB_TEXT}
      />
      <div className={styles.trendPage}>
        <div>
          <HeaderTab isJobRankingLinkShow={isJobRankingLinkShow} />
          <Tag
            title={title}
            description={description}
            keywords={keywords}
            canonical={canonicalUrl}
          />
          <h1 className={styles.trendPage__heading}>
            {targetName} doda掲載中求人の傾向
            <p className="trendPage__heading--publicationDate">
              更新日：{publicationDate}
            </p>
          </h1>
          <p className={styles.trendPage__leadText}>
            dodaに掲載している「{targetName}
            」の求人に関するデータを、下のカテゴリごとにご紹介します。
          </p>
          <AnchorLink />
          <JobCountBargraphArea targetName={targetName} />
          {isJobRankingLinkShow && <OtherPageLink targetName={targetName} />}
          {!isLogin && (
            <MemberRegistForFooter
              memberRegistLink={memberRegistLink}
              guideLoginLink={guideLoginLink}
              introLink={introLink}
            />
          )}
          {faqAreaShowFlg && (
            <FaqArea
              targetName={targetName}
              requirementsShowFlg={requirementsShowFlg}
              incomeShowFlg={incomeShowFlg}
              workplaceEnvironmentShowFlg={workplaceEnvironmentShowFlg}
              holidayAndWayOfWorkingShowFlg={holidayAndWayOfWorkingShowFlg}
              employmentStatusShowFlg={employmentStatusShowFlg}
              rankingList={rankingList}
            />
          )}
          {faqAreaShowFlg && (
            <FaqLd
              targetName={targetName}
              requirementsShowFlg={requirementsShowFlg}
              incomeShowFlg={incomeShowFlg}
              workplaceEnvironmentShowFlg={workplaceEnvironmentShowFlg}
              holidayAndWayOfWorkingShowFlg={holidayAndWayOfWorkingShowFlg}
              employmentStatusShowFlg={employmentStatusShowFlg}
              rankingList={rankingList}
            />
          )}
          <div className={styles.trendPage__annotation}>
            <p>
              {ANNOTATION_TEXT_1}
              <br />
              {ANNOTATION_TEXT_2}
            </p>
          </div>
        </div>
        <div>
          {!isLogin && (
            <MemberRegistForRight
              memberRegistLink={memberRegistLink}
              guideLoginLink={guideLoginLink}
              introLink={introLink}
            />
          )}
          <DiagnosisTools />
          <OtherJobContentInfo title={OTHER_JOB_TREND_TITLE} />
        </div>
      </div>
    </>
  );
};
